import { CounselingTypeEnum } from 'app/Bookings/constants'
import { specialties, specialtyValueMapping } from 'app/SeFriendly/constants'

export const QUESTIONNAIRE_MAX_SKIP_COUNT = 4

export enum QuestionnaireFlowTypes {
  GENERIC = 'generic',
  GENERIC_CHILD_THERAPY = 'genericChildTherapy',
  SERVICE = 'service',
  CHILD_THERAPY = 'childTherapy',
}

export enum QuestionnairePageTypes {
  SELECT_SERVICE_TYPE = 'select-service',
  SELECT_AGE_GROUP = 'select-age-group',
  SELECT_LOCATION = 'select-location',
  SELECT_INSURANCE = 'select-insurance',
}

export const QuestionnairePages = {
  SELECT_SERVICE_TYPE: {
    pageName: QuestionnairePageTypes.SELECT_SERVICE_TYPE,
    headerTitle: `Let’s help you find a therapist in`,
    subTextEmoji: '👋',
    subText:
      "State regulations require us to book you with a provider licensed in your state. We'll show you who is available to work with you.",
    coverImageSrc: '/images/lady1WithVase.svg',
  },
  SELECT_AGE_GROUP: {
    pageName: QuestionnairePageTypes.SELECT_AGE_GROUP,
    headerTitle: "We've got you covered. How old are they?",
    subText:
      'Our providers use evidence-based approaches to help kids work through age-specific concerns.',
    coverImageSrc: '/images/lady2WithVase.svg',
  },
  SELECT_AGE_GROUP_CHILD_FLOW: {
    pageName: QuestionnairePageTypes.SELECT_AGE_GROUP,
    headerTitle: "We've got you covered. How old are they?",
    subText:
      'Our providers use evidence-based approaches to help kids work through age-specific concerns.',
    coverImageSrc: '/images/lady2WithVase.svg',
  },
  SELECT_LOCATION: {
    pageName: QuestionnairePageTypes.SELECT_LOCATION,
    headerTitle: 'Where do you live?',
    subTextEmoji: '👋',
    subText:
      "State regulations require us to book you with a provider licensed in your state. We'll show you who is available to work with you.",
    coverImageSrc: '/images/lady2WithVase.svg',
    ctaText: 'Continue to insurance',
  },
  SELECT_LOCATION_FOR_CHILD: {
    pageName: QuestionnairePageTypes.SELECT_LOCATION,
    headerTitle: 'Where do they live?',
    subTextEmoji: '👋',
    subText:
      'State regulations only allow bookings with a provider licensed in the same state. Knowing where your child lives helps us find someone that can work with them.',
    coverImageSrc: '/images/lady2WithVase.svg',
    ctaText: 'Continue to insurance',
  },
  SELECT_INSURANCE: {
    pageName: QuestionnairePageTypes.SELECT_INSURANCE,
    headerTitle: 'Would you like to use insurance?',
    subText:
      'Thriveworks accepts most major insurances, with most clients often owing just a small co-pay per session.',
    coverImageSrc: '/images/lady3WithVase.svg',
    ctaText: 'View providers',
  },
}

// ** Below is the mapping of questionnaire flow types to pages **
// GENERIC: Questtionnaire starts with service type selection page
// GENERIC_CHILD_THERAPY: This flow is subset of GENERIC flow. Select Service Type → Select Child Age Group → Select Location → Select Insurance.
// SERVICE: Questionnaire starts with location selection page (with selected service type present in query params)
// CHILD_THERAPY: Questionnaire starts with child age group selection page (with "childTherapy" service type present in query params)
export const QUESTIONNAIRE_PAGE_CONFIG: Record<
  QuestionnaireFlowTypes,
  QuestionnairePageType[]
> = {
  [QuestionnaireFlowTypes.GENERIC]: [
    QuestionnairePages.SELECT_SERVICE_TYPE,
    QuestionnairePages.SELECT_INSURANCE,
  ],
  [QuestionnaireFlowTypes.GENERIC_CHILD_THERAPY]: [
    QuestionnairePages.SELECT_AGE_GROUP,
    QuestionnairePages.SELECT_INSURANCE,
  ],
  [QuestionnaireFlowTypes.SERVICE]: [
    QuestionnairePages.SELECT_LOCATION,
    QuestionnairePages.SELECT_INSURANCE,
  ],
  [QuestionnaireFlowTypes.CHILD_THERAPY]: [
    QuestionnairePages.SELECT_AGE_GROUP_CHILD_FLOW,
    QuestionnairePages.SELECT_LOCATION,
    QuestionnairePages.SELECT_INSURANCE,
  ],
}

export const questionnaireAgeGroups = [
  {
    value: '0-5 years old',
    icon: { src: '/images/agegroup1.svg', alt: 'age group 0-5' },
  },
  {
    value: '6-10 years old',
    icon: { src: '/images/agegroup2.svg', alt: 'age group 6-10' },
  },
  {
    value: '11-13 years old',
    icon: { src: '/images/agegroup3.svg', alt: 'age group 11-13' },
  },
  {
    value: '14-17 years old',
    icon: { src: '/images/agegroup4.svg', alt: 'age group 14-17' },
  },
]

export const questionnaireServices = [
  {
    serviceType: CounselingTypeEnum.INDIVIDUAL,
    mainText: 'Individual therapy',
    subText:
      'Work one-on-one with a talk therapist to achieve personal growth.',
    icon: { src: '/images/service1.svg', alt: 'Individual therapy service' },
  },
  {
    serviceType: CounselingTypeEnum.MEDICATION,
    mainText: 'Psychiatry / medication',
    subText: 'Reduce and manage symptoms with medication management.',
    icon: { src: '/images/service2.svg', alt: 'Psychiatry/medication service' },
  },
  {
    serviceType: CounselingTypeEnum.COUPLES,
    mainText: 'Couples therapy',
    mainTextMobile: 'Couples/marriage therapy',
    subText:
      'Navigate challenges and improve communication in your relationship.',
    icon: { src: '/images/service3.svg', alt: 'Couples therapy service' },
  },
  {
    serviceType: specialtyValueMapping[specialties.CHILDRENS_THERAPY],
    mainText: 'Child/teen therapy',
    subText: 'Get compassionate, expert help for your kids.',
    icon: { src: '/images/service4.svg', alt: 'Child/teen therapy service' },
  },
  {
    serviceType: CounselingTypeEnum.FAMILY,
    mainText: 'Family therapy',
    subText: 'Improve communication and foster a healthier family dynamic.',
    icon: { src: '/images/service5.svg', alt: 'Family therapy service' },
  },
]
